import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { 
  Activity, 
  TrendingUp, 
  Calendar, 
  Footprints, 
  Flame, 
  Heart, 
  Timer,
  Plus,
  Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActivityTracker = () => {
  const [activityHistory, setActivityHistory] = useState([]);
  const [todayActivity, setTodayActivity] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7); // Default to 7 days
  
  // Form state for manual entry
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    steps: 0,
    distance_miles: 0,
    avg_heart_rate: 0,
    calories_burned: 0,
    active_minutes: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadActivityData();
  }, [period]);

  const loadActivityData = async () => {
    setLoading(true);
    try {
      const [historyRes, todayRes] = await Promise.all([
        axios.get(`${API}/activity/history?days=${period}`),
        axios.get(`${API}/activity/today`)
      ]);

      setActivityHistory(historyRes.data.activity_data || []);
      setSummary(historyRes.data.summary || {});
      setTodayActivity(todayRes.data);

      // Pre-fill form with today's data if it exists
      if (todayRes.data && todayRes.data.data_source !== 'none') {
        setFormData({
          date: todayRes.data.date,
          steps: todayRes.data.steps || 0,
          distance_miles: todayRes.data.distance_miles || 0,
          avg_heart_rate: todayRes.data.avg_heart_rate || 0,
          calories_burned: todayRes.data.calories_burned || 0,
          active_minutes: todayRes.data.active_minutes || 0
        });
      }
    } catch (error) {
      console.error('Failed to load activity data:', error);
      toast.error('Failed to load activity data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'date' ? value : (parseFloat(value) || 0)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await axios.post(`${API}/activity/log`, formData);
      toast.success(response.data.message || 'Activity logged successfully');
      await loadActivityData();
    } catch (error) {
      console.error('Failed to log activity:', error);
      toast.error(error.response?.data?.detail || 'Failed to log activity');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteActivity = async (activityDate) => {
    try {
      await axios.delete(`${API}/activity/${activityDate}`);
      toast.success('Activity deleted successfully');
      await loadActivityData();
    } catch (error) {
      console.error('Failed to delete activity:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete activity');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="w-6 h-6" />
            Activity Tracker
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Log and track your daily physical activity
          </p>
        </div>
      </div>

      <Tabs defaultValue="log" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="log">Log Activity</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="summary">Summary</TabsTrigger>
        </TabsList>

        {/* Log Activity Tab */}
        <TabsContent value="log" className="space-y-4">
          <Card className="bg-blue-950/50 border-blue-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Manual Activity Entry
              </CardTitle>
              <CardDescription className="text-gray-400">
                Enter your activity data for any day
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date" className="text-white">Date</Label>
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      max={new Date().toISOString().split('T')[0]}
                      className="bg-blue-900/50 border-blue-700 text-white"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="steps" className="text-white flex items-center gap-1">
                      <Footprints className="w-4 h-4" />
                      Steps
                    </Label>
                    <Input
                      id="steps"
                      name="steps"
                      type="number"
                      min="0"
                      value={formData.steps}
                      onChange={handleInputChange}
                      className="bg-blue-900/50 border-blue-700 text-white"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <Label htmlFor="distance_miles" className="text-white">
                      Distance (miles)
                    </Label>
                    <Input
                      id="distance_miles"
                      name="distance_miles"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.distance_miles}
                      onChange={handleInputChange}
                      className="bg-blue-900/50 border-blue-700 text-white"
                      placeholder="0.0"
                    />
                  </div>

                  <div>
                    <Label htmlFor="calories_burned" className="text-white flex items-center gap-1">
                      <Flame className="w-4 h-4" />
                      Calories Burned
                    </Label>
                    <Input
                      id="calories_burned"
                      name="calories_burned"
                      type="number"
                      min="0"
                      value={formData.calories_burned}
                      onChange={handleInputChange}
                      className="bg-blue-900/50 border-blue-700 text-white"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <Label htmlFor="active_minutes" className="text-white flex items-center gap-1">
                      <Timer className="w-4 h-4" />
                      Active Minutes
                    </Label>
                    <Input
                      id="active_minutes"
                      name="active_minutes"
                      type="number"
                      min="0"
                      value={formData.active_minutes}
                      onChange={handleInputChange}
                      className="bg-blue-900/50 border-blue-700 text-white"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <Label htmlFor="avg_heart_rate" className="text-white flex items-center gap-1">
                      <Heart className="w-4 h-4" />
                      Avg Heart Rate (bpm)
                    </Label>
                    <Input
                      id="avg_heart_rate"
                      name="avg_heart_rate"
                      type="number"
                      min="0"
                      max="250"
                      value={formData.avg_heart_rate}
                      onChange={handleInputChange}
                      className="bg-blue-900/50 border-blue-700 text-white"
                      placeholder="0"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={saving}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? 'Saving...' : 'Save Activity'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <div className="flex gap-2 mb-4">
            <Button
              variant={period === 7 ? "default" : "outline"}
              onClick={() => setPeriod(7)}
              className="flex-1"
            >
              7 Days
            </Button>
            <Button
              variant={period === 30 ? "default" : "outline"}
              onClick={() => setPeriod(30)}
              className="flex-1"
            >
              30 Days
            </Button>
            <Button
              variant={period === 90 ? "default" : "outline"}
              onClick={() => setPeriod(90)}
              className="flex-1"
            >
              90 Days
            </Button>
          </div>

          <div className="space-y-3">
            {activityHistory.length === 0 ? (
              <Card className="bg-blue-950/50 border-blue-800">
                <CardContent className="p-6 text-center text-gray-400">
                  No activity data logged yet. Start logging your daily activity!
                </CardContent>
              </Card>
            ) : (
              activityHistory.map((activity) => (
                <Card key={activity.date} className="bg-blue-950/50 border-blue-800 hover:border-blue-600 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <Calendar className="w-4 h-4 text-blue-400" />
                          <span className="text-white font-medium">
                            {formatDate(activity.date)}
                          </span>
                          <Badge variant="secondary" className="text-xs">
                            {activity.data_source || 'manual'}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {activity.steps > 0 && (
                            <div className="flex items-center gap-2">
                              <Footprints className="w-4 h-4 text-green-400" />
                              <div>
                                <p className="text-xs text-gray-400">Steps</p>
                                <p className="text-white font-semibold">{activity.steps.toLocaleString()}</p>
                              </div>
                            </div>
                          )}

                          {activity.distance_miles > 0 && (
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-blue-400" />
                              <div>
                                <p className="text-xs text-gray-400">Distance</p>
                                <p className="text-white font-semibold">{activity.distance_miles} mi</p>
                              </div>
                            </div>
                          )}

                          {activity.calories_burned > 0 && (
                            <div className="flex items-center gap-2">
                              <Flame className="w-4 h-4 text-orange-400" />
                              <div>
                                <p className="text-xs text-gray-400">Calories</p>
                                <p className="text-white font-semibold">{activity.calories_burned}</p>
                              </div>
                            </div>
                          )}

                          {activity.active_minutes > 0 && (
                            <div className="flex items-center gap-2">
                              <Timer className="w-4 h-4 text-purple-400" />
                              <div>
                                <p className="text-xs text-gray-400">Active</p>
                                <p className="text-white font-semibold">{activity.active_minutes} min</p>
                              </div>
                            </div>
                          )}

                          {activity.avg_heart_rate > 0 && (
                            <div className="flex items-center gap-2">
                              <Heart className="w-4 h-4 text-red-400" />
                              <div>
                                <p className="text-xs text-gray-400">Heart Rate</p>
                                <p className="text-white font-semibold">{activity.avg_heart_rate} bpm</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteActivity(activity.date)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-950/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Summary Tab */}
        <TabsContent value="summary" className="space-y-4">
          <Card className="bg-blue-950/50 border-blue-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Activity Summary ({period} days)
              </CardTitle>
              <CardDescription className="text-gray-400">
                Your activity statistics for the selected period
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-blue-900/30 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-500/20 rounded-lg">
                        <Footprints className="w-6 h-6 text-green-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Total Steps</p>
                        <p className="text-2xl font-bold text-white">
                          {summary.total_steps?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Avg: {Math.round(summary.avg_steps_per_day || 0).toLocaleString()}/day
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-900/30 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <TrendingUp className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Total Distance</p>
                        <p className="text-2xl font-bold text-white">
                          {summary.total_distance_miles || 0} mi
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {((summary.total_distance_miles || 0) * 1.60934).toFixed(1)} km
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-900/30 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-500/20 rounded-lg">
                        <Flame className="w-6 h-6 text-orange-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Total Calories</p>
                        <p className="text-2xl font-bold text-white">
                          {summary.total_calories?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Avg: {Math.round((summary.total_calories || 0) / (summary.days_logged || 1))}/day
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-900/30 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-500/20 rounded-lg">
                        <Timer className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Total Active Time</p>
                        <p className="text-2xl font-bold text-white">
                          {summary.total_active_minutes || 0} min
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {((summary.total_active_minutes || 0) / 60).toFixed(1)} hours
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-900/30 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-500/20 rounded-lg">
                        <Calendar className="w-6 h-6 text-yellow-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Days Logged</p>
                        <p className="text-2xl font-bold text-white">
                          {summary.days_logged || 0}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          out of {period} days
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ActivityTracker;
